import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/client';
import type { DirectoryUser } from '@/types';

/**
 * Picks a real, known Key Travel person for owner reassignment — never free text. An admin types a
 * name or email, sees actual matches from the search endpoint (itself gated on the report server's
 * own user catalogue, not the whole Entra tenant — see ReportServerUserDirectory), and picks one;
 * there is no path that lets a typo or a made-up id become the new owner. The server validates the
 * choice again independently — this picker narrows what an admin is offered, it is not the
 * enforcement.
 *
 * Debounces its own search rather than importing the shared debounce hook the parameter editor
 * uses: that hook belongs to a different area of this codebase being changed at the same time, and
 * a few lines of local `useEffect` here is cheaper than a cross-cutting dependency on it.
 */
export function OwnerPicker({
  value,
  onChange,
  placeholder = 'Search for a person by name or email…'
}: {
  value: DirectoryUser | null;
  onChange: (user: DirectoryUser | null) => void;
  placeholder?: string;
}) {
  const api = useApi();
  const [text, setText] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(text.trim()), 250);
    return () => clearTimeout(t);
  }, [text]);

  const { data: matches, isFetching, isError, error } = useQuery({
    queryKey: ['user-search', debounced],
    queryFn: () => api.searchUsers(debounced),
    enabled: debounced.length >= 2,
    // Matches the other live-search pickers (PickupHeadOfficePicker, SearchablePicker): a failed
    // search should say so immediately, not spend several seconds retrying with backoff before
    // admitting it could not check.
    retry: false
  });

  if (value) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className="recipient-chip" style={{ background: 'var(--kt-green-bg)', borderColor: 'var(--kt-green-mid)' }}>
          {value.displayName}
          {value.mail && <span style={{ color: 'var(--kt-grey-500)' }}> &nbsp;({value.mail})</span>}
          <span
            className="x"
            onClick={() => { onChange(null); setText(''); setOpen(false); }}
          >
            &times;
          </span>
        </span>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        className="input"
        type="text"
        placeholder={placeholder}
        value={text}
        onChange={e => { setText(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && debounced.length >= 2 && (
        <div
          className="card"
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
            marginTop: 4, maxHeight: 260, overflowY: 'auto', padding: 4
          }}
        >
          {isFetching && <div style={{ padding: '8px 10px', color: 'var(--kt-grey-500)' }}>Searching…</div>}
          {/* Deliberately not folded into "no matches" below: a directory outage and "nobody is
              named that" are different facts, and telling them apart is the whole point of this
              picker over the free-text box it replaced. See AdminController.SearchUsers and
              ReportServerUserDirectory. */}
          {!isFetching && isError && (
            <div style={{ padding: '8px 10px', color: 'var(--kt-red)' }}>
              {/* The server distinguishes "the report server's own user list is unreadable" from
                  "name/email search specifically (Graph) is down" — two different systems, two
                  different fixes — so that distinction is shown verbatim rather than flattened
                  into one generic message here. */}
              {describeSearchError(error)}
            </div>
          )}
          {!isFetching && !isError && (matches ?? []).length === 0 && (
            <div style={{ padding: '8px 10px', color: 'var(--kt-grey-500)' }}>No matches for &ldquo;{debounced}&rdquo;.</div>
          )}
          {!isFetching && !isError && (matches ?? []).map(u => (
            <div
              key={u.objectId}
              // onMouseDown, not onClick: the input's onBlur fires first on a click and would
              // close this list before the click could land on it.
              onMouseDown={() => { onChange(u); setText(''); setOpen(false); }}
              style={{ padding: '8px 10px', cursor: 'pointer', borderRadius: 'var(--radius-sm)' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--kt-grey-50)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <div style={{ fontWeight: 600 }}>{u.displayName}</div>
              {u.mail && <div style={{ fontSize: '.82rem', color: 'var(--kt-grey-500)' }}>{u.mail}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Same convention as Admin.tsx's describeError: strip the "API 502: " prefix, keep the rest verbatim. */
function describeSearchError(error: unknown): string {
  return error instanceof Error
    ? error.message.replace(/^API \d+: /, '')
    : 'Could not search the directory right now. Try again shortly.';
}
